import type { BoxProps } from '@hover/blueprint';
import { Box } from '@hover/blueprint';
import { useMachine } from '@xstate/react';
import React, { useContext, useEffect, useRef } from 'react';

import {
  LANDSCAPE_TOOLBAR_MENU_WIDTH,
  LANDSCAPE_TOOLBAR_WIDTH,
} from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import { Menu } from 'src/modules/designStudio/constants';
import type { DesignBuilding } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import type { ExtendedMacheteViewer } from 'src/modules/designStudio/helpers/machete';
import type {
  Geometry,
  Metadata,
} from 'src/modules/designStudio/hooks/useFetchModelData/types';
import { createMachine } from 'src/modules/designStudio/machines/building';
import type { Textures } from 'src/modules/designStudio/machines/productCatalog';
import {
  MacheteBuildingContext,
  historyContext,
} from 'src/modules/designStudio/providers';
import type { TrackingMetadata } from 'src/modules/designStudio/types';
import { useIsLandscape, useMode, useSplit } from 'src/shared/hooks';

interface BuildingProps {
  jobId?: number;
  canvasHeight: string;
  setLoadingBuilding: (loadingBuilding: boolean) => void;
  setViewer?: (viewer: ExtendedMacheteViewer) => void;
  menu: Menu | null;
  textures: Textures;
  geometry: Geometry;
  metadata: Metadata;
  trackingMetadata?: TrackingMetadata;
}

const Building = React.memo(
  ({
    jobId,
    setLoadingBuilding,
    canvasHeight,
    setViewer,
    menu,
    textures,
    geometry,
    metadata,
    trackingMetadata,
  }: BuildingProps) => {
    const mode = useMode();

    const { client: clientPromise } = useSplit();
    const [isLandscape] = useIsLandscape();
    const canvasRef = useRef<HTMLDivElement | null>(null);

    const { setBuilding } = useContext(MacheteBuildingContext);

    const { historyState } = useContext(historyContext);

    const { history } = historyState;

    const [buildingMachineState, send] = useMachine(() => {
      const buildingMachine = createMachine();
      return buildingMachine.withContext({
        ...buildingMachine.context,
        jobId,
        canvasRef,
        textures,
        geometry,
        metadata,
        splitClient: clientPromise,
        mode,
        trackingMetadata,
      });
    });

    const building: DesignBuilding = buildingMachineState.context
      .building as DesignBuilding;
    const { viewer, sentHistoryLoadedEvent } = buildingMachineState.context;

    useEffect(() => {
      setBuilding(building);
    }, [setBuilding, building]);

    const historyDesignConfig = history?.[0]?.design_config || null;

    if (!sentHistoryLoadedEvent && historyDesignConfig) {
      send({
        type: 'HISTORY_LOADED',
        savedStylesToUpdate: historyDesignConfig,
      });
    }

    useEffect(() => {
      if (viewer && setViewer) {
        setViewer(viewer);
      }
    }, [setViewer, viewer]);

    useEffect(() => {
      send({
        type: 'CHANGE_VIEW',
        menu,
        canvasHeight,
      });
    }, [send, canvasHeight, menu, canvasRef]);

    useEffect(() => {
      if (building) {
        setLoadingBuilding(false);
      }
    }, [setLoadingBuilding, building]);

    const containerStyles: BoxProps = {
      overflow: 'none',
      position: 'absolute',
      top: 0,
      right: 0,
    };
    if (isLandscape) {
      const expandedMenuWidth = `calc(100% - ${
        LANDSCAPE_TOOLBAR_WIDTH + LANDSCAPE_TOOLBAR_MENU_WIDTH
      }px)`;
      const noMenuWidth = '100%';
      containerStyles.width =
        menu && menu !== Menu.SHARE ? expandedMenuWidth : noMenuWidth;
      containerStyles.height = '100vh';
    } else {
      containerStyles.width = '100vw';
      containerStyles.height = canvasHeight;
    }
    containerStyles.transition = menu ? 'width 600ms ease-out' : '';

    /**
     * We pass in templateDesignConfigFlag as a parameter so that when it loads we will re-render each config componenet,
     * which will in turn invoke updateStyles to apply the style to the building.
     *
     * We show the Inspire Me image on top of the Machete render whenever it's available.
     */
    return (
      <Box>
        <Box testId="canvas-container" {...containerStyles}>
          <Box height="100%" ref={canvasRef} width="100%" />
        </Box>
      </Box>
    );
  },
);

export { Building, BuildingProps };
